'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import type { CoordinatePlanePayload } from './types';
import { loadJSXGraph } from '@/lib/loadJSXGraph';

interface CoordinatePlaneEntryProps {
  payload: CoordinatePlanePayload;
}

function sanitizePayload(payload: CoordinatePlanePayload): Required<CoordinatePlanePayload> {
  const xMin = Number.isFinite(payload.xMin) ? payload.xMin : -10;
  const xMaxRaw = Number.isFinite(payload.xMax) ? payload.xMax : 10;
  const yMin = Number.isFinite(payload.yMin) ? payload.yMin : -10;
  const yMaxRaw = Number.isFinite(payload.yMax) ? payload.yMax : 10;

  return {
    xMin,
    xMax: xMaxRaw > xMin ? xMaxRaw : xMin + 1,
    yMin,
    yMax: yMaxRaw > yMin ? yMaxRaw : yMin + 1,
    xStep: payload.xStep && payload.xStep > 0 ? payload.xStep : 1,
    yStep: payload.yStep && payload.yStep > 0 ? payload.yStep : 1,
    points: payload.points ?? [],
    segments: payload.segments ?? [],
    lines: payload.lines ?? [],
    curves: payload.curves ?? [],
  };
}

function lineFromStandard(a: number, bStd: number, c: number): { p1: [number, number]; p2: [number, number] } | null {
  if (Math.abs(a) < 1e-9 && Math.abs(bStd) < 1e-9) return null;

  if (Math.abs(bStd) > 1e-9) {
    const yAt0 = c / bStd;
    const yAt1 = (c - a) / bStd;
    return { p1: [0, yAt0], p2: [1, yAt1] };
  }

  const x = c / a;
  return { p1: [x, 0], p2: [x, 1] };
}

function evaluateCurveAt(
  curve: Required<CoordinatePlanePayload>['curves'][number],
  x: number,
): number | null {
  if (!Number.isFinite(x)) return null;

  if (curve.type === 'linear' && Number.isFinite(curve.m) && Number.isFinite(curve.b)) {
    return Number(curve.m) * x + Number(curve.b);
  }

  if (curve.type === 'quadratic' && Number.isFinite(curve.a) && Number.isFinite(curve.b) && Number.isFinite(curve.c)) {
    const a = Number(curve.a);
    const b = Number(curve.b);
    const c = Number(curve.c);
    return a * x * x + b * x + c;
  }

  if (curve.type === 'absolute_value' && Number.isFinite(curve.a) && Number.isFinite(curve.h) && Number.isFinite(curve.k)) {
    const a = Number(curve.a);
    const h = Number(curve.h);
    const k = Number(curve.k);
    return a * Math.abs(x - h) + k;
  }

  return null;
}

function collectBounds(data: Required<CoordinatePlanePayload>) {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  const consider = (x: number, y: number) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  };

  data.points.forEach((point) => consider(point.x, point.y));
  data.segments.forEach((segment) => {
    consider(segment.x1, segment.y1);
    consider(segment.x2, segment.y2);
  });
  // Always include origin so axes show 0 even when auto-scaling.
  consider(0, 0);
  data.lines.forEach((line) => {
    if (line.form === 'two_points') {
      consider(Number(line.x1), Number(line.y1));
      consider(Number(line.x2), Number(line.y2));
      return;
    }

    if (line.form === 'slope_intercept' && Number.isFinite(line.m) && Number.isFinite(line.b)) {
      const m = Number(line.m);
      const b = Number(line.b);
      consider(0, b);
      consider(1, m + b);
      if (Math.abs(m) > 1e-9) {
        consider(-b / m, 0);
      }
      return;
    }

    if (line.form === 'standard' && Number.isFinite(line.a) && Number.isFinite(line.bStd) && Number.isFinite(line.c)) {
      const a = Number(line.a);
      const bStd = Number(line.bStd);
      const c = Number(line.c);
      if (Math.abs(a) > 1e-9) {
        consider(c / a, 0);
      }
      if (Math.abs(bStd) > 1e-9) {
        consider(0, c / bStd);
      }
    }
  });
  data.curves.forEach((curve) => {
    const sampleXs = [data.xMin, data.xMax, 0];

    if (curve.type === 'quadratic' && Number.isFinite(curve.a) && Number.isFinite(curve.b)) {
      const a = Number(curve.a);
      const b = Number(curve.b);
      if (Math.abs(a) > 1e-9) {
        sampleXs.push(-b / (2 * a));
      }
    }

    if (curve.type === 'absolute_value' && Number.isFinite(curve.h)) {
      sampleXs.push(Number(curve.h));
    }

    sampleXs.forEach((x) => {
      const y = evaluateCurveAt(curve, x);
      if (y !== null) {
        consider(x, y);
      }
    });
  });

  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
    return null;
  }

  return { minX, maxX, minY, maxY };
}

function roundDownToStep(value: number, step: number) {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) return value;
  return Math.floor(value / step) * step;
}

function roundUpToStep(value: number, step: number) {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) return value;
  return Math.ceil(value / step) * step;
}

function computeAutoBounds(data: Required<CoordinatePlanePayload>) {
  const bounds = collectBounds(data);
  if (!bounds) return null;

  const spanX = Math.max(bounds.maxX - bounds.minX, 0);
  const spanY = Math.max(bounds.maxY - bounds.minY, 0);
  const paddingX = Math.max(spanX * 0.2, 1);
  const paddingY = Math.max(spanY * 0.2, 1);
  const minSpan = 4;

  const targetSpanX = Math.max(spanX + paddingX * 2, minSpan);
  const targetSpanY = Math.max(spanY + paddingY * 2, minSpan);
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  const rawXMin = centerX - targetSpanX / 2;
  const rawXMax = centerX + targetSpanX / 2;
  const rawYMin = centerY - targetSpanY / 2;
  const rawYMax = centerY + targetSpanY / 2;

  const stepX = data.xStep || 1;
  const stepY = data.yStep || 1;

  return {
    xMin: roundDownToStep(rawXMin, stepX),
    xMax: roundUpToStep(rawXMax, stepX),
    yMin: roundDownToStep(rawYMin, stepY),
    yMax: roundUpToStep(rawYMax, stepY),
  };
}

export function CoordinatePlaneEntry({ payload }: CoordinatePlaneEntryProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<any>(null);
  const boardId = useMemo(() => `jxg-coordinate-${Math.random().toString(36).slice(2)}`, []);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      if (!containerRef.current) return;

      const data = sanitizePayload(payload);
      const autoBounds = computeAutoBounds(data);
      const useAutoBounds = autoBounds
        && (
          (autoBounds.xMax - autoBounds.xMin) < (data.xMax - data.xMin) * 0.9
          || (autoBounds.yMax - autoBounds.yMin) < (data.yMax - data.yMin) * 0.9
        );
      const xMin = useAutoBounds ? autoBounds!.xMin : data.xMin;
      const xMax = useAutoBounds ? autoBounds!.xMax : data.xMax;
      const yMin = useAutoBounds ? autoBounds!.yMin : data.yMin;
      const yMax = useAutoBounds ? autoBounds!.yMax : data.yMax;
      const JXG = await loadJSXGraph();
      if (!isMounted || !containerRef.current) return;

      if (boardRef.current) {
        JXG.JSXGraph.freeBoard(boardRef.current);
        boardRef.current = null;
      }

      const board = JXG.JSXGraph.initBoard(boardId, {
        boundingbox: [xMin, yMax, xMax, yMin],
        keepaspectratio: true,
        axis: true,
        grid: true,
        showNavigation: false,
        showCopyright: false,
        pan: { enabled: false },
        zoom: { enabled: false },
      });
      boardRef.current = board;

      const defaultAxis = board.defaultAxes;
      if (defaultAxis?.x) {
        defaultAxis.x.setAttribute({
          strokeColor: '#4b5563',
          strokeWidth: 2,
          ticks: { strokeColor: '#9ca3af', label: { color: '#374151' } },
        });
        defaultAxis.x.defaultTicks?.setAttribute({ label: { color: '#374151' } });
      }
      if (defaultAxis?.y) {
        defaultAxis.y.setAttribute({
          strokeColor: '#4b5563',
          strokeWidth: 2,
          ticks: { strokeColor: '#9ca3af', label: { color: '#374151' } },
        });
        defaultAxis.y.defaultTicks?.setAttribute({ label: { color: '#374151' } });
      }

      data.points.forEach((point) => {
        board.create('point', [point.x, point.y], {
          name: point.label || '',
          size: 4,
          face: 'o',
          fillColor: point.style === 'hollow' ? '#ffffff' : (point.color || '#1f2937'),
          strokeColor: point.color || '#1f2937',
          fixed: true,
          highlight: false,
          label: {
            cssStyle: `color:${point.color || '#3f3f46'};font-size:12px;`,
            offset: [8, -8],
          },
        });
      });

      data.segments.forEach((segment) => {
        const p1 = board.create('point', [segment.x1, segment.y1], {
          visible: false,
          fixed: true,
          name: '',
          withLabel: false,
        });
        const p2 = board.create('point', [segment.x2, segment.y2], {
          visible: false,
          fixed: true,
          name: '',
          withLabel: false,
        });
        board.create('segment', [p1, p2], {
          strokeColor: segment.color || '#1d4ed8',
          strokeWidth: 2.5,
          fixed: true,
          highlight: false,
          dash: segment.dashed ? 2 : 0,
        });

        if (segment.label) {
          board.create('text', [
            (segment.x1 + segment.x2) / 2,
            (segment.y1 + segment.y2) / 2,
            segment.label,
          ], {
            anchorX: 'middle',
            fontSize: 12,
            color: segment.color || '#1d4ed8',
            fixed: true,
            highlight: false,
          });
        }
      });

      data.lines.forEach((line) => {
        let p1: [number, number] | null = null;
        let p2: [number, number] | null = null;

        if (line.form === 'slope_intercept' && Number.isFinite(line.m) && Number.isFinite(line.b)) {
          const m = Number(line.m);
          const b = Number(line.b);
          p1 = [xMin, m * xMin + b];
          p2 = [xMax, m * xMax + b];
        } else if (line.form === 'standard' && Number.isFinite(line.a) && Number.isFinite(line.bStd) && Number.isFinite(line.c)) {
          const result = lineFromStandard(Number(line.a), Number(line.bStd), Number(line.c));
          if (result) {
            p1 = result.p1;
            p2 = result.p2;
          }
        } else if (
          line.form === 'two_points'
          && Number.isFinite(line.x1)
          && Number.isFinite(line.y1)
          && Number.isFinite(line.x2)
          && Number.isFinite(line.y2)
        ) {
          p1 = [Number(line.x1), Number(line.y1)];
          p2 = [Number(line.x2), Number(line.y2)];
        }

        if (!p1 || !p2) return;

        const a = board.create('point', p1, {
          visible: false,
          fixed: true,
          name: '',
          withLabel: false,
        });
        const b = board.create('point', p2, {
          visible: false,
          fixed: true,
          name: '',
          withLabel: false,
        });

        const stroke = line.color || '#b45309';
        const lineEl = board.create('line', [a, b], {
          strokeColor: stroke,
          strokeWidth: 2.3,
          fixed: true,
          highlight: false,
          dash: line.dashed ? 2 : 0,
        });

        if (line.label) {
          board.create('text', [
            (p1[0] + p2[0]) / 2,
            (p1[1] + p2[1]) / 2,
            line.label,
          ], {
            anchorX: 'middle',
            fontSize: 12,
            color: stroke,
            fixed: true,
            highlight: false,
          });
        }

        lineEl.setAttribute({ fixed: true });
      });

      data.curves.forEach((curve) => {
        if (curve.type === 'linear' && (!Number.isFinite(curve.m) || !Number.isFinite(curve.b))) return;
        if (curve.type === 'quadratic' && (!Number.isFinite(curve.a) || !Number.isFinite(curve.b) || !Number.isFinite(curve.c))) return;
        if (curve.type === 'absolute_value' && (!Number.isFinite(curve.a) || !Number.isFinite(curve.h) || !Number.isFinite(curve.k))) return;

        const fn = (x: number) => evaluateCurveAt(curve, x) ?? NaN;
        const stroke = curve.color || '#c026d3';
        board.create('functiongraph', [fn, xMin, xMax], {
          strokeColor: stroke,
          strokeWidth: 2.6,
          dash: curve.dashed ? 2 : 0,
          fixed: true,
          highlight: false,
        });

        if (curve.label) {
          const labelX = xMax - (xMax - xMin) * 0.2;
          const labelY = fn(labelX);
          if (Number.isFinite(labelY)) {
            board.create('text', [labelX, labelY, curve.label], {
              anchorX: 'left',
              fontSize: 12,
              color: stroke,
              fixed: true,
              highlight: false,
            });
          }
        }
      });

      board.update();
    };

    void init();

    return () => {
      isMounted = false;
      if (boardRef.current && window.JXG?.JSXGraph) {
        window.JXG.JSXGraph.freeBoard(boardRef.current);
        boardRef.current = null;
      }
    };
  }, [boardId, payload]);

  return (
    <div
      id={boardId}
      ref={containerRef}
      style={{ width: '100%', maxWidth: 620, aspectRatio: '1 / 1', margin: '0 auto', backgroundColor: '#ffffff' }}
    />
  );
}
